import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { FiLock, FiShield, FiArrowLeft } from "react-icons/fi";
import { apiService } from "../api/apiService";
import { useNavigate } from "react-router-dom";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import styles from "./AdminPinGate.module.css";
import ZmianaPinuAdministratora from "./PinNaZadanie/ZmianaPinuAdministratora";
import { DLUGOSC_PINU, DLUGOSC_DAWNEGO_PINU, dlugoscZOdpowiedzi } from "./PinNaZadanie/dlugoscPinu";
import { useAuth } from "../hooks/useAuth";
import * as toastModul from "../context/ToastContext";

/* [PIN-ZARZAD, decyzja właściciela 2026-09-27] „pin zostaw tylko dla admina 5 cyfry to jest ok".
   BYŁO: stałe 4 pola. TERAZ: tyle pól, ile ma PIN tego konta (serwer: `dlugoscPinu` — 5 dla PIN-u ustawionego wg nowej
   reguły, 4 dla PIN-u sprzed zmiany, który działa do wymiany). Po podaniu starego PIN-u serwer mówi `wymagaZmiany`
   i bramka proponuje nowy (formularz ZmianaPinuAdministratora) — „Później" wpuszcza do pracy bez zmiany.
   Konto BEZ rangi administratora serwer przepuszcza (`verified: true, niewymagany: true`) — nigdy nie widzi klawiatury. */
const pustyPin = (dlugosc) => Array.from({ length: dlugosc }, () => "");

/* Atrapy ToastContext w testach innych ekranów nie znają tego hooka — bez niego potwierdzenie po prostu się nie pokaże
   (zachowanie sprzed poprawki), zamiast wywracać bramkę. */
const useToastJesliJest = typeof toastModul.useToastOpcjonalny === "function" ? toastModul.useToastOpcjonalny : () => null;

/** Zdanie awaryjne, gdy serwer nie przysłał własnego — mówi KTO nadaje PIN i GDZIE. */
const KOMUNIKAT_BRAK_PINU = "Twoje konto nie ma indywidualnego PIN-u administratora, a panel wymaga własnego PIN-u "
    + "od każdej osoby, która do niego wchodzi. Poproś administratora o nadanie PIN-u (Uprawnienia i dostęp → "
    + "Użytkownicy → PIN i skrzynki). PIN systemowy nie otwiera już panelu.";

/* [A15-13] BYŁO ŹLE: o wejściu na ekrany za PIN-em rozstrzygała PRZEGLĄDARKA — stan startowy
   brano z `sessionStorage.adminPinVerified`, a odpowiedź backendu służyła wyłącznie do tego,
   żeby okienko PIN-u ewentualnie pominąć („NIE degradujemy"). Wystarczyło wpisać jedną wartość
   w narzędziach deweloperskich, żeby wejść bez PIN-u.
   SKUTEK DLA UŻYTKOWNIKA: PIN był barierą przed przypadkowym kliknięciem, a nie zabezpieczeniem.
   NAPRAWA: o dostępie rozstrzyga SERWER — bramka czeka na `/health/admin-pin-status`, który
   czyta podpisane, httpOnly ciasteczko (nie do podrobienia z przeglądarki). Operacje schowane
   za PIN-em mają dodatkowo własną bramkę po stronie backendu (RequireAdminPinFilter).
   ZMIANA ZACHOWANIA: kto dotąd omijał okienko przez sessionStorage — poda PIN. Kto PIN podaje
   normalnie, nie zauważy różnicy (ciasteczko ważne 8 h i odnawiane przy każdym wejściu — dotąd
   zgoda ginęła wraz z kartą przeglądarki, więc jest raczej wygodniej).
   ŚCIEŻKA PRZEJŚCIA: PIN indywidualny i globalny działają jak dotąd; gdy w systemie nie ma
   ŻADNEGO PIN-u, serwer odpowiada `verified: true` i bramka przepuszcza — nikt nie zostaje
   zamknięty przed panelem tylko dlatego, że PIN-u nigdy nie skonfigurowano. */
const AdminPinGate = ({ children }) => {
    /* [PIN-ZARZAD poprawka 2026-09-28, uwaga przeglądu] „delegaci bez rangi nigdy nie widzą okna PIN”. BYŁO: bramka pytała
       serwer o status także dla konta bez rangi, a gdy zapytanie padło (sieć, 5xx), gałąź awaryjna pokazywała KLAWIATURĘ —
       delegat z kluczem (np. /admin/migracja-bazy) wpisywał kod, serwer odpowiadał `niewymagany`, a bramka mówiła
       „Nieprawidłowy PIN” przy każdym kodzie. TERAZ: konto, o którym wiemy, że nie ma rangi administratora (useAuth),
       przechodzi od razu, bez zapytania — tak samo jak na serwerze (RequireAdminPinFilter). Tryb podglądu konta nie zmienia
       `user` (to wciąż konto administratora), więc administrator w podglądzie dalej podaje PIN. */
    const auth = useAuth();
    const bezRangi = !!auth?.user && auth.user.type !== 3;
    // [PIN-ZARZAD poprawka 2 2026-09-28, uwaga przeglądu] Potwierdzenie zapisu nowego PIN-u — patrz karta niżej.
    const toast = useToastJesliJest();
    // null = jeszcze nie wiemy (czekamy na werdykt serwera), true/false = werdykt.
    const [verified, setVerified] = useState(() => (bezRangi ? true : null));
    // [brief 2026-09-05] „Nawet jak ktoś ma dostęp do sekcji admina = musi mieć nadany indywidualny PIN."
    // Serwer mówi `wymaganyIndywidualny`: konto nie ma własnego PIN-u, a inne konta już mają — żaden kod
    // nie zadziała, więc zamiast klawiatury pokazujemy KARTĘ z zdaniem, kto ma nadać PIN. Klawiatura, na
    // której nic nie działa, to dokładnie ten telefon do IT, którego nie chcemy.
    const [brakIndywidualnego, setBrakIndywidualnego] = useState(null); // null | string (zdanie z serwera)
    const [tryb, setTryb] = useState(null); // Rozruch | MaIndywidualny | …
    const [dlugosc, setDlugosc] = useState(DLUGOSC_PINU);
    const [pin, setPin] = useState(() => pustyPin(DLUGOSC_PINU));
    // null = nic do zmiany; napis = serwer poprosił o nowy, 5-cyfrowy PIN (zdanie z serwera).
    const [zmianaPinu, setZmianaPinu] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [shake, setShake] = useState(false);
    const inputsRef = useRef([]);
    const navigate = useNavigate();
    // Zabezpieczenia przed wielokrotnym auto-submitem tego samego kodu (inaczej jeden błąd PIN
    // generował kilka prób na backendzie i wyczerpywał limit → blokada 429 nawet dla poprawnego PIN-u).
    const submittingRef = useRef(false);
    const lastTriedRef = useRef("");
    // Serwer nie zdołał wydać trwałego ciasteczka (padnięty key-ring DataProtection). Wtedy —
    // i tylko wtedy — utrzymujemy dostęp w pamięci komponentu, żeby administrator nie utknął
    // w pętli „podaj PIN" przy każdym przejściu w panelu.
    const cookielessSessionRef = useRef(false);
    // [PIN-ZARZAD poprawka 2026-09-28] PIN, który właśnie otworzył bramkę — formularz nowego PIN-u przekazuje go serwerowi
    // jako „obecny PIN” (bez niego zostawiona otwarta sesja podmieniałaby PIN). Żyje tylko w pamięci komponentu.
    const zweryfikowanyPinRef = useRef("");

    useEffect(() => {
        if (bezRangi) return undefined;
        let cancelled = false;
        apiService.adminPinStatus()
            .then(res => {
                if (cancelled) return;
                if (res.data?.wymaganyIndywidualny) setBrakIndywidualnego(res.data?.message || KOMUNIKAT_BRAK_PINU);
                setTryb(res.data?.tryb || null);
                const d = dlugoscZOdpowiedzi(res.data);
                setDlugosc(d);
                setPin(pustyPin(d));
                setVerified(res.data?.verified === true);
            })
            .catch(() => {
                // Nie udało się zapytać serwera — pokazujemy PIN. To nie jest blokada:
                // użytkownik podaje PIN i wchodzi tak samo jak zawsze.
                if (!cancelled && !cookielessSessionRef.current) setVerified(false);
            });
        return () => { cancelled = true; };
    }, [bezRangi]);

    // Auto-focus first input on mount
    useEffect(() => {
        if (!verified) {
            setTimeout(() => inputsRef.current[0]?.focus(), 100);
        }
    }, [verified]);

    const handleChange = useCallback((index, value) => {
        // Only digits
        const digit = value.replace(/\D/g, "").slice(-1);
        setError("");

        setPin(prev => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });

        // Move to next input
        if (digit && index < dlugosc - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    }, [dlugosc]);

    const handleKeyDown = useCallback((index, e) => {
        if (e.key === "Backspace" && !pin[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    }, [pin]);

    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, dlugosc);
        if (pasted.length === dlugosc) {
            setPin(pasted.split(""));
            inputsRef.current[dlugosc - 1]?.focus();
        }
    }, [dlugosc]);

    // Auto-submit gdy wszystkie cyfry wpisane (5, a dla PIN-u sprzed zmiany 4) — dokładnie JEDNA próba na komplet cyfr.
    useEffect(() => {
        const code = pin.join("");
        if (code.length !== dlugosc || pin.length !== dlugosc || pin.some(d => d === "")) return;
        if (submittingRef.current || lastTriedRef.current === code) return;
        lastTriedRef.current = code;
        submittingRef.current = true;

        const verify = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await apiService.verifyAdminPin(code);
                if (res.data?.wymaganyIndywidualny) {
                    setBrakIndywidualnego(res.data?.message || KOMUNIKAT_BRAK_PINU);
                    setPin(pustyPin(dlugosc));
                    return;
                }
                // [PIN-ZARZAD poprawka 2026-09-28] Serwer: „PIN Cię nie dotyczy” (konto bez rangi) — wejście, nie „zły PIN”.
                if (res.data?.niewymagany) {
                    setVerified(true);
                    return;
                }
                if (res.data?.valid) {
                    zweryfikowanyPinRef.current = code;
                    // persisted === false → serwer nie wydał ciasteczka; dostęp utrzymujemy
                    // wyłącznie w pamięci tego komponentu (nic, co da się podrobić w DevTools).
                    if (res.data?.persisted === false) cookielessSessionRef.current = true;
                    // [PIN-ZARZAD 2026-09-27] Stary, 4-cyfrowy PIN przeszedł — zaproponuj nowy (bez blokowania pracy).
                    if (res.data?.wymagaZmiany) setZmianaPinu(res.data?.message || "");
                    setVerified(true);
                    return;
                }
                // Błędny PIN — czyść POLA NATYCHMIAST (nie w setTimeout), by efekt nie wystrzelił ponownie.
                setError("Nieprawidłowy PIN");
                setShake(true);
                setPin(pustyPin(dlugosc));
                lastTriedRef.current = "";
                setTimeout(() => {
                    setShake(false);
                    inputsRef.current[0]?.focus();
                }, 600);
            } catch (err) {
                const status = err?.response?.status;
                setError(status === 429
                    ? "Zbyt wiele prób. Odczekaj kilka minut i spróbuj ponownie."
                    : "Błąd weryfikacji PIN");
                setPin(pustyPin(dlugosc));
                lastTriedRef.current = "";
                inputsRef.current[0]?.focus();
            } finally {
                setLoading(false);
                submittingRef.current = false;
            }
        };

        verify();
    }, [pin, dlugosc]);

    // [PIN-ZARZAD 2026-09-27] Po starym PIN-ie — karta z propozycją nowego, 5-cyfrowego. Zapis albo „Później" = panel.
    if (verified === true && zmianaPinu !== null) {
        return (
            <div className={styles.overlay} data-testid="pin-zmiana-na-piec-cyfr">
                <motion.div
                    className={styles.card}
                    initial={{ opacity: 0, y: 20, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                    <div className={styles.iconWrap}>
                        <div className={styles.iconRing}>
                            <FiShield size={28} />
                        </div>
                    </div>
                    <h2 className={styles.title}>Ustaw nowy PIN</h2>
                    <p className={styles.subtitle}>PIN administratora ma teraz 5 cyfr</p>
                    <ZmianaPinuAdministratora
                        komunikat={zmianaPinu || null}
                        rozruch={tryb === "Rozruch"}
                        obecnyPin={zweryfikowanyPinRef.current}
                        /* [PIN-ZARZAD poprawka 2 2026-09-28, uwaga przeglądu] BYŁO: `onZapisano={() => setZmianaPinu(null)}` —
                           zdanie serwera („Zapisano nowy, 5-cyfrowy PIN… stary przestał działać”) szło do kosza, a karta po prostu
                           znikała: nie było wiadomo, czy PIN się zmienił i którym wchodzić jutro. TERAZ zdanie serwera = dymek. */
                        onZapisano={(zdanie) => {
                            setZmianaPinu(null);
                            if (toast?.showToast) toast.showToast(zdanie, "success", 10);
                        }}
                        onPozniej={() => setZmianaPinu(null)}
                    />
                </motion.div>
            </div>
        );
    }

    if (verified === true) return children;

    // Czekamy na werdykt serwera — nie mrugamy okienkiem PIN-u komuś, kto ma ważne ciasteczko.
    if (verified === null) return null;

    // Konto BEZ własnego PIN-u przy innych z własnym — karta zamiast klawiatury (żaden kod nie zadziała).
    if (brakIndywidualnego) {
        return (
            <div className={styles.overlay} data-testid="pin-brak-indywidualnego">
                <motion.div
                    className={styles.card}
                    initial={{ opacity: 0, y: 20, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                    <div className={styles.iconWrap}>
                        <div className={styles.iconRing}>
                            <FiLock size={28} />
                        </div>
                    </div>
                    <h2 className={styles.title}>Panel administracyjny</h2>
                    <p className={styles.subtitle}>Brak indywidualnego PIN-u</p>
                    <p className={styles.hint} style={{ marginTop: 0 }}>{brakIndywidualnego}</p>
                    <button
                        className={styles.backBtn}
                        onClick={() => navigate(localizedPath("dashboard"))}
                        type="button"
                    >
                        <FiArrowLeft size={14} />
                        Wróć do dashboardu
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className={styles.overlay}>
            <motion.div
                className={`${styles.card} ${shake ? styles.shake : ""}`}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
                {/* Icon */}
                <div className={styles.iconWrap}>
                    <div className={styles.iconRing}>
                        <FiShield size={28} />
                    </div>
                </div>

                {/* Title */}
                <h2 className={styles.title}>Panel administracyjny</h2>
                <p className={styles.subtitle}>Wprowadź PIN aby uzyskać dostęp</p>

                {/* PIN Inputs */}
                <div className={styles.pinRow}>
                    {pin.map((digit, i) => (
                        <input
                            key={i}
                            ref={el => inputsRef.current[i] = el}
                            type="password"
                            inputMode="numeric"
                            maxLength={1}
                            className={`${styles.pinInput} ${digit ? styles.pinInputFilled : ""} ${error ? styles.pinInputError : ""}`}
                            value={digit}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                            onPaste={i === 0 ? handlePaste : undefined}
                            disabled={loading}
                            autoComplete="off"
                            aria-label={`Cyfra ${i + 1} z ${dlugosc} PIN-u`}
                        />
                    ))}
                </div>

                {/* Error */}
                {error && (
                    <motion.p
                        className={styles.error}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <FiLock size={13} />
                        {error}
                    </motion.p>
                )}

                {/* Loading indicator */}
                {loading && (
                    <div className={styles.loadingRow}>
                        <div className={styles.spinner} />
                        <span>Weryfikacja...</span>
                    </div>
                )}

                {/* Back button */}
                <button
                    className={styles.backBtn}
                    onClick={() => navigate(localizedPath("dashboard"))}
                    type="button"
                >
                    <FiArrowLeft size={14} />
                    Wróć do dashboardu
                </button>

                {/* Footer hint */}
                <p className={styles.hint}>
                    {tryb === "Rozruch"
                        ? "Wprowadź PIN systemowy — do czasu nadania pierwszych PIN-ów indywidualnych"
                        : "Wprowadź swój indywidualny PIN"}
                    {dlugosc === DLUGOSC_DAWNEGO_PINU
                        ? " (4 cyfry — stary PIN; po wejściu ustawisz nowy, 5-cyfrowy)."
                        : " (5 cyfr)."}
                </p>
            </motion.div>
        </div>
    );
};

export default AdminPinGate;
